import { makeAutoObservable, runInAction } from 'mobx';

import { IBaseStore } from '~base/types/BaseTypes';
import FileHelper from '~helpers/FileHelper';
import AttachedFilesService from '~modules/attachedFiles/AttachedFilesService';
import { AttachedImagesDeletionForm } from '~modules/attachedFiles/forms/AttachedImagesDeletionForm';
import { UploadedFileModel } from '~modules/attachedFiles/models/UploadedFileModel';
import { UploadingStateEnum } from '~modules/attachedFiles/types/UploadingStateEnum';
import { FileModeEnum } from '~modules/fileTypes/types/FileModeEnum';

export class AttachedFilesStore implements IBaseStore {
  uploadedFiles: UploadedFileModel[] = [];
  uploadedFilesLoading: boolean = false;
  uploadedFilesLoaded: boolean = false;

  private service: AttachedFilesService;

  constructor() {
    makeAutoObservable(this);

    this.service = new AttachedFilesService();
  }

  // API

  getAttachedDocuments = (
    mode: FileModeEnum,
    documentTypeId: number,
    documentId: number | undefined,
    documentTitle: string | undefined,
    fixedTypeId?: number | null,
  ) => {
    this.uploadedFilesLoading = true;

    this.service
      .getAttachedDocuments(mode, documentTypeId, documentId, documentTitle, fixedTypeId)
      .then((items) =>
        runInAction(() => {
          this.uploadedFiles = this.service.prepareUploadedFiles(items);
          this.uploadedFilesLoaded = true;
        }),
      )
      .catch(() =>
        runInAction(() => {
          this.uploadedFilesLoaded = false;
        }),
      )
      .finally(() =>
        runInAction(() => {
          this.uploadedFilesLoading = false;
        }),
      );
  };

  uploadFiles = (
    documentTypeId: number,
    documentId: number | undefined,
    documentTitle: string | undefined,
    fixedTypeId: number | null | undefined,
    files: File[],
    onSuccess?: () => void,
  ) => {
    const fileIdMap = FileHelper.createFileIdMap(files);

    this.uploadedFiles = this.service.addLoadedFiles(this.uploadedFiles, files, fileIdMap);

    const requests = files.map((file) => {
      const tempFileId = fileIdMap.get(file)!;
      return this.service
        .addAttachedDocument(documentTypeId, documentId, documentTitle, fixedTypeId, file)
        .then((data) =>
          runInAction(() => {
            this.uploadedFiles = this.service.replaceUploadedFile(this.uploadedFiles, tempFileId, data);
          }),
        )
        .catch(() =>
          runInAction(() => {
            this.uploadedFiles = this.service.replaceCaughtFile(this.uploadedFiles, tempFileId);
          }),
        );
    });

    void Promise.all(requests).then(() => {
      onSuccess?.();
    });
  };

  deleteUploadedFile = (file: UploadedFileModel, onSuccess?: () => void) => {
    if (file.uploadingState === UploadingStateEnum.ERROR) {
      this.uploadedFiles = this.service.removeFileFromState(this.uploadedFiles, file);
      return;
    }

    if (file.fileId === null) {
      // Если файл еще не загружен на сервер, просто удаляем из локального состояния
      runInAction(() => {
        onSuccess?.();
        this.uploadedFiles = this.service.removeFileFromState(this.uploadedFiles, file);
      });
      return;
    }

    // Устанавливаем состояние загрузки перед удалением
    this.uploadedFiles = this.service.setFileDeleting(this.uploadedFiles, file);

    // Удаляем файл через API
    this.service
      .deleteAttachedDocument(file.fileId)
      .then(() =>
        runInAction(() => {
          onSuccess?.();
          this.uploadedFiles = this.service.removeFileFromState(this.uploadedFiles, file);
        }),
      )
      .catch(() =>
        runInAction(() => {
          // В случае ошибки возвращаем файл в исходное состояние
          this.uploadedFiles = this.service.restoreFileToOriginalState(this.uploadedFiles, file);
        }),
      );
  };

  deleteAttachedImages = (form: AttachedImagesDeletionForm, onSuccess?: () => void) => {
    // Получаем список ID файлов для удаления из формы
    const fileIdsArray = Object.entries(form.imagesToDelete)
      .filter(([, value]) => value)
      .map(([key]) => Number(key));

    if (fileIdsArray.length === 0) {
      return;
    }

    // Находим файлы, которые будут удалены, и сохраняем их исходное состояние
    const filesToDelete = this.uploadedFiles.filter(
      (file) => file.fileId !== null && fileIdsArray.includes(file.fileId),
    );

    // Устанавливаем состояние загрузки для выбранных файлов
    runInAction(() => {
      this.uploadedFiles = this.service.setFilesDeleting(this.uploadedFiles, fileIdsArray);
    });

    // Удаляем файлы через API
    this.service
      .deleteAttachedDocuments(form)
      .then(() =>
        runInAction(() => {
          onSuccess?.();
        }),
      )
      .catch(() =>
        runInAction(() => {
          // В случае ошибки возвращаем файлы в исходное состояние
          this.uploadedFiles = this.service.restoreFilesToOriginalState(this.uploadedFiles, filesToDelete);
        }),
      );
  };

  // RESET

  resetUploadedFiles = () => {
    this.uploadedFiles = [];
    this.uploadedFilesLoading = false;
    this.uploadedFilesLoaded = false;
  };

  reset = () => {
    this.resetUploadedFiles();
  };

  //OTHERS

  prepareImagesToDeletionFormValues = (data: UploadedFileModel[]) => {
    return this.service.prepareImagesToDeletionFormValues(data);
  };
}
